/**
 * RadarChart — 6-axis English ability visualization. Reads RadarData
 * returned from GET /api/school/students/{id}/radar.
 *
 * Null values render as 0 on the chart with a faded styling so the
 * visual difference between "0 actual" and "not yet assessed" is
 * still readable in the surrounding context (handbook §9 Page 3).
 */
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart as RechartsRadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import type { RadarData } from "../api/school";

const SKILL_LABEL_ZH: Record<string, string> = {
  listening: "听力",
  speaking: "口语",
  reading: "阅读",
  writing: "写作",
  vocabulary: "词汇",
  grammar: "语法",
};

interface RadarChartProps {
  data: RadarData;
  height?: number;
  /** Label language. Defaults to Chinese for Bosong demo. */
  lang?: "zh" | "en";
}

export function RadarChart({ data, height = 360, lang = "zh" }: RadarChartProps) {
  const allUnassessed = data.axes.every((a) => a.score === null);

  const chartData = data.axes.map((a) => ({
    skill: lang === "zh" ? SKILL_LABEL_ZH[a.skill] ?? a.skill : a.skill,
    score: a.score ?? 0,
    _isNull: a.score === null,
  }));

  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={height}>
        <RechartsRadarChart data={chartData} outerRadius="75%">
          <PolarGrid stroke="#cbd5e1" />
          <PolarAngleAxis dataKey="skill" tick={{ fill: "#475569", fontSize: 13 }} />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            tick={{ fill: "#94a3b8", fontSize: 10 }}
            tickCount={6}
          />
          <Radar
            name={lang === "zh" ? "能力评分" : "Score"}
            dataKey="score"
            stroke="#0ea5e9"
            fill="#0ea5e9"
            fillOpacity={0.35}
          />
          <Tooltip
            formatter={(value, _name, item) => {
              const isNull = (item?.payload as { _isNull?: boolean } | undefined)?._isNull;
              const display = isNull
                ? lang === "zh"
                  ? "未评估"
                  : "Not assessed"
                : (value as number);
              return [display, ""];
            }}
          />
        </RechartsRadarChart>
      </ResponsiveContainer>
      {allUnassessed && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-slate-400 text-sm italic">
            {lang === "zh" ? "暂无评估数据" : "No assessment data yet"}
          </span>
        </div>
      )}
    </div>
  );
}
